import { useEffect, useRef } from "react";
import LogsIcon from "../../../assets/logs-icon.svg";
import { TimelineItem as TimelineItemType } from "../../../types/Dashboard.types";
import { EmptyState } from "../../ui/EmptyState/EmptyState";

type EUAIActTimelineProps = {
  timeline: TimelineItemType[];
};

type TimelineItem = {
  date: string;
  description: string;
  status: "completed" | "upcoming";
};

export function EUAIActTimeline({ timeline }: EUAIActTimelineProps) {
  const firstUpcomingRef = useRef<HTMLDivElement>(null);

  const displayTimeline: TimelineItem[] = timeline.map((item) => ({
    date: new Date(item.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    description: item.description,
    status: item.isPast ? "completed" : "upcoming",
  }));

  const firstUpcomingIndex = displayTimeline.findIndex(
    (item) => item.status === "upcoming"
  );

  useEffect(() => {
    if (firstUpcomingRef.current) {
      firstUpcomingRef.current.scrollIntoView({ block: "center" });
    }
  }, [timeline]);

  if (displayTimeline.length === 0) {
    return (
      <div className="bg-white rounded-lg ring-1 ring-gray-250v2 shadow-sm p-6 flex flex-col gap-4 h-[380px]">
        <h3 className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-gray-550 text-left flex-shrink-0">
          EU AI ACT TIMELINE
        </h3>
        <EmptyState
          icon={<img src={LogsIcon} alt="No timeline" className="w-[28px] h-[36px]" />}
          title="No Timeline Events"
          description="Timeline events will appear here once available."
          classes="!p-0 !border-0 !shadow-none flex-1"
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-250v2 shadow-sm p-6 flex flex-col gap-4 h-[380px]">
      <h3 className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-gray-550 text-left flex-shrink-0">
        EU AI ACT TIMELINE
      </h3>

      <div className="flex flex-col overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-1">
        {displayTimeline.map((item, index) => (
          <div
            key={index}
            ref={index === firstUpcomingIndex ? firstUpcomingRef : null}
            className="flex gap-3 pb-[15px] last:pb-0"
          >
            {/* Timeline dot and line */}
            <div className="flex flex-col items-center">
              <div
                className={`w-3 h-3 rounded-lg shadow-sm flex-shrink-0 ${
                  item.status === "completed" ? "bg-cyan-200" : "bg-gray-400"
                }`}
              />
              <div className="w-[2px] flex-1 pt-1">
                <div className="w-full h-full bg-gray-250v2" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col gap-1 text-left">
              <div className="flex items-center justify-between">
                <p className="font-inter font-medium text-base leading-6 text-gray-900">
                  {item.date}
                </p>
                {/* Status badge */}
                <span
                  className={`px-[10px] py-[2px] rounded-[10px] font-inter font-medium text-xs leading-4 whitespace-nowrap ${
                    item.status === "completed"
                      ? "bg-cyan-100 text-cyan-700"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {item.status === "completed" ? "Completed" : "Upcoming"}
                </span>
              </div>
              <p className="font-inter font-normal text-sm leading-5 text-gray-800">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
